import { faMeta } from '@fortawesome/free-brands-svg-icons';
import { faArrowUpRightFromSquare, faRectangleAd } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SimpleGrid } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { OmniCampaign } from '@feathr/blackbox';
import { Button, CardV2 as Card, SelectableCard } from '@feathr/components';
import { useLocalUrl } from '@feathr/extender/state';

import { useChannelsCard } from './ChannelsCard.hooks';
import {
  getDisplayCreativesTooltip,
  getMetaCreativesTooltip,
  getMetaDefaultDescription,
  getMetaSetupDescription,
} from './ChannelsCard.utils';

interface IChannelsCardProps {
  campaign: OmniCampaign;
  /** Whether any Display creatives exist on the campaign */
  hasDisplayCreatives?: boolean;
  /** Whether any Meta creatives exist on the campaign */
  hasMetaCreatives?: boolean;
}

function ChannelsCard({
  campaign,
  hasDisplayCreatives = false,
  hasMetaCreatives = false,
}: Readonly<IChannelsCardProps>): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const {
    displaySelected,
    metaSelected,
    handleDisplayChange,
    handleMetaChange,
    metaCardState,
    metaLockedTooltip,
    isChannelSelectionDisabled,
  } = useChannelsCard(campaign);

  const isMetaLocked = metaCardState === 'locked';
  const isMetaSetup = metaCardState === 'setup';
  const isMetaDisabledMonetization = metaCardState === 'disabled_monetization';

  const setupButton = (
    <Button
      href={localUrl('settings/integrations/meta')}
      name={'meta-setup'}
      suffix={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
      target={'_blank'}
      type={'secondary'}
    >
      {t('Set up')}
    </Button>
  );

  return (
    <Card>
      <Card.Header
        description={t(
          'Choose the platforms where you want your Pixl+ campaign to run. You can select one or both channels.',
        )}
        title={t('Channels')}
      />
      <Card.Content addVerticalGap={true}>
        <SimpleGrid cols={2}>
          <SelectableCard
            checked={displaySelected}
            contentOrientation={'vertical'}
            data-name={'channel-display'}
            description={t(
              'Reach your audience through display ads on websites across the internet',
            )}
            disabled={isChannelSelectionDisabled || hasDisplayCreatives}
            name={'channel-display'}
            onChange={handleDisplayChange}
            prefix={<FontAwesomeIcon icon={faRectangleAd} />}
            theme={'cyan'}
            title={t('Display Advertising')}
            tooltip={hasDisplayCreatives ? getDisplayCreativesTooltip(t) : undefined}
          />
          <SelectableCard
            checked={metaSelected}
            contentOrientation={'vertical'}
            data-name={'channel-meta'}
            description={isMetaSetup ? getMetaSetupDescription(t) : getMetaDefaultDescription(t)}
            disabled={
              isMetaSetup ||
              isChannelSelectionDisabled ||
              isMetaDisabledMonetization ||
              hasMetaCreatives
            }
            locked={isMetaLocked}
            name={'channel-meta'}
            onChange={handleMetaChange}
            prefix={<FontAwesomeIcon icon={faMeta} />}
            suffix={isMetaSetup ? setupButton : undefined}
            theme={'cyan'}
            title={t('Meta Advertising')}
            tooltip={hasMetaCreatives ? getMetaCreativesTooltip(t) : metaLockedTooltip}
          />
        </SimpleGrid>
      </Card.Content>
    </Card>
  );
}

export default observer(ChannelsCard);
